import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import AiAdvisorChat from "@/components/AiAdvisorChat";
import PriceEstimateTool from "@/components/PriceEstimateTool";
import RentalEstimateTool from "@/components/RentalEstimateTool";
import { RequestCallButton } from "@/components/RequestCallButton";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  FileText,
  AlignLeft,
  ExternalLink,
  Trash2,
  Loader2,
  Clock,
  Lock,
  Check,
  CreditCard,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const money = (n) => `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const severityStyle = {
  identified: { icon: AlertTriangle, cls: "text-amber-600", badge: "bg-amber-100 text-amber-700", label: "Identified" },
  not_identified: { icon: CheckCircle2, cls: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700", label: "Not identified" },
  informational: { icon: Info, cls: "text-sky-600", badge: "bg-sky-100 text-sky-700", label: "Info" },
};

const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

function buildReportOutline(report) {
  if (!report) return "";
  const pages = report.pages || [];
  const head = report.address ? `Due Diligence Report for ${report.address}\n` : "";
  if (pages.length === 0) return head + "No due diligence content was returned.";
  const parts = pages.map((p) => {
    const title = p.title || p.web_title || "Section";
    const desc = p.short_description ? `: ${stripHtml(p.short_description)}` : "";
    const cons = Array.isArray(p.considerations) && p.considerations.length
      ? "\n   " + p.considerations.map((c) => `- [${c.severity || "info"}] ${stripHtml(c.text)}`).join("\n   ")
      : "";
    return `• ${title}${desc}${cons}`;
  });
  return head + "Sections:\n" + parts.join("\n");
}

function TextReportView({ report }) {
  const pages = report.pages || [];
  if (pages.length === 0) {
    return <p className="text-sm text-slate-500">No due diligence content was returned for this address.</p>;
  }
  return (
    <div className="space-y-4" data-testid="text-report-view">
      {pages.map((page, i) => (
        <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100">
            <p className="font-semibold text-slate-800">{page.title || page.web_title}</p>
            {page.short_description && <p className="text-xs text-slate-500 mt-0.5">{page.short_description}</p>}
          </div>
          <div className="p-4 space-y-3">
            {page.description && (
              <div className="advisor-md text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: page.description }} />
            )}
            {Array.isArray(page.considerations) && page.considerations.length > 0 && (
              <div className="space-y-2">
                {page.considerations.map((c, j) => {
                  const st = severityStyle[c.severity] || severityStyle.informational;
                  const Icon = st.icon;
                  return (
                    <div key={j} className="flex items-start gap-2 bg-slate-50/70 border border-slate-100 rounded-lg px-3 py-2">
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${st.cls}`} />
                      <div className="min-w-0">
                        <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mb-1 ${st.badge}`}>{st.label}</span>
                        <div className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: c.text }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PropertyResearcher() {
  const [address, setAddress] = useState("");
  const [submittingText, setSubmittingText] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const viewerRef = useRef(null);

  const openReport = (report) => {
    setSelectedReport(report);
    setTimeout(() => {
      viewerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // Subscription state
  const [sub, setSub] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingInterval, setBillingInterval] = useState("year");
  const [managingPlan, setManagingPlan] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);

  const handleApplyReferral = async () => {
    const code = referralCode.trim();
    if (!code) {
      toast.error("Please enter a referral code.");
      return;
    }
    setApplyingCode(true);
    try {
      const res = await api.post("/referral/apply", { code });
      const { partner_name, benefit_type, discount_percent, trial_days } = res.data;
      let msg = `Referral code applied${partner_name ? ` (${partner_name})` : ""}.`;
      if (benefit_type === "price" && discount_percent > 0) msg += ` ${discount_percent}% discount added.`;
      else if (benefit_type === "trial" && trial_days > 0) msg += ` ${trial_days}-day free trial added.`;
      toast.success(msg);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply that referral code.");
    } finally {
      setApplyingCode(false);
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get("/subscription/status");
      setSub(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSub(false);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get("/research/reports");
      setReports(res.data.reports || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // Poll payment status after returning from Stripe
  const pollPayment = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setProcessingPayment(false);
      toast.error("Payment status check timed out. If you paid, please refresh in a moment.");
      return;
    }
    try {
      const res = await api.get(`/subscription/checkout/status/${sessionId}`);
      const { status, payment_status } = res.data;
      if (payment_status === "paid") {
        setProcessingPayment(false);
        toast.success("Subscription active! You now have access to AI Buyers Agent.");
        await fetchStatus();
        return;
      }
      if (status === "expired") {
        setProcessingPayment(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch (err) {
      setProcessingPayment(false);
      toast.error("Could not verify payment status.");
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    fetchReports();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      setProcessingPayment(true);
      pollPayment(sessionId);
      // clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchStatus, fetchReports, pollPayment]);

  const handleSubscribe = async () => {
    setCheckingOut(true);
    try {
      const res = await api.post("/subscription/checkout", {
        origin_url: window.location.origin,
        interval: billingInterval,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout.");
        setCheckingOut(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setCheckingOut(false);
    }
  };

  const handleSwitchToAnnual = async () => {
    setManagingPlan(true);
    try {
      await api.post("/subscription/switch-to-annual");
      toast.success("You're now on the Annual plan — enjoy the savings!");
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch to annual.");
    } finally {
      setManagingPlan(false);
    }
  };

  const handleGenerateText = async () => {
    const trimmed = address.trim();
    if (!trimmed) {
      toast.error("Please enter a property address (including suburb and state).");
      return;
    }
    setSubmittingText(true);
    try {
      const res = await api.post("/research/homesnoop/report-text", { address: trimmed });
      const newReport = res.data;
      setReports((prev) => [newReport, ...prev]);
      openReport(newReport);
      setAddress("");
      toast.success("Report ready.");
      fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create report. Please try again.";
      toast.error(msg);
    } finally {
      setSubmittingText(false);
    }
  };


  const handleDelete = async (reportId) => {
    try {
      await api.delete(`/research/reports/${reportId}`);
      setReports((prev) => prev.filter((r) => r.report_id !== reportId));
      if (selectedReport?.report_id === reportId) setSelectedReport(null);
      toast.success("Report removed.");
    } catch (err) {
      toast.error("Could not delete report.");
    }
  };

  const hasAccess = sub?.has_access;
  const reason = sub?.reason;
  const plans = sub?.plans || {
    month: { base: 90, gst: 9, total: 99, payable: 99, interval: "month" },
    year: { base: 540, gst: 54, total: 594, payable: 594, interval: "year" },
  };
  const plan = plans[billingInterval] || plans.month;
  const monthlyPlan = plans.month;
  const annualPlan = plans.year;
  // Effective monthly cost when billed annually, and the % saved vs paying monthly for 12 months.
  const annualPerMonth = annualPlan.payable / 12;
  const annualSavingsPct = Math.round((1 - annualPlan.payable / (monthlyPlan.payable * 12)) * 100);
  const discount = sub?.referral_discount_percent || 0;
  const textInfo = sub?.text_reports || {};
  const hasTextLimit = textInfo.limit != null;
  const textRemaining = textInfo.remaining;
  const textLimitReached = hasTextLimit && textRemaining <= 0;
  const trialAvailable = sub?.trial_available;
  const trialDays = sub?.trial_available_days || 7;
  const isTrialing = sub?.is_trialing;
  const trialEnd = sub?.trial_end;
  const currentInterval = sub?.current_interval;
  const cancelAtPeriodEnd = sub?.cancel_at_period_end;
  const showAnnualNudge =
    reason === "subscription" && currentInterval === "month" && !cancelAtPeriodEnd && !nudgeDismissed;

  const Header = (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">AI Buyers Agent</h1>
      <p className="text-slate-500 mt-1">Generate detailed property due-diligence reports.</p>
    </div>
  );

  if (loadingSub || processingPayment) {
    return (
      <div className="space-y-6">
        {Header}
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-3" />
            <p>{processingPayment ? "Confirming your payment..." : "Loading..."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PAYWALL
  if (!hasAccess) {
    return (
      <div className="space-y-6">
        {Header}
        <Card className="overflow-hidden">
          <div className="bg-[#547d91] px-6 py-5 text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold">Unlock AI Buyers Agent</h2>
              <p className="text-white/80 text-sm">
                {trialAvailable ? `Start your ${trialDays}-day free trial — cancel anytime.` : "Subscribe to generate Due Diligence reports."}
              </p>
            </div>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Billing interval toggle */}
            <div className="inline-flex items-center rounded-xl bg-slate-100 p-1" data-testid="billing-interval-toggle">
              <button
                type="button"
                onClick={() => setBillingInterval("month")}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${billingInterval === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingInterval("year")}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${billingInterval === "year" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="billing-annual-btn"
              >
                Annual
                {annualSavingsPct > 0 && (
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">Save {annualSavingsPct}%</span>
                )}
              </button>
            </div>

            <div className="flex items-end gap-2" data-testid="plan-price">
              {discount > 0 && discount < 100 ? (
                <>
                  <span className="text-slate-400 line-through text-xl">{money(plan.total)}</span>
                  <span className="text-4xl font-bold text-slate-900">{money(plan.payable)}</span>
                </>
              ) : (
                <span className="text-4xl font-bold text-slate-900">{money(plan.payable)}</span>
              )}
              <span className="text-slate-500 mb-1">/ {billingInterval === "year" ? "year" : "month"} (incl. GST)</span>
            </div>

            {billingInterval === "year" && (
              <p className="-mt-3 text-sm text-slate-500" data-testid="annual-per-month">
                Just {money(annualPerMonth)}/mo billed annually — {annualSavingsPct}% off the monthly price.
              </p>
            )}

            {trialAvailable && (
              <div className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-1.5" data-testid="trial-badge">
                <Sparkles className="w-4 h-4" /> {trialDays}-day free trial — you won't be charged until it ends
              </div>
            )}

            {discount > 0 && discount < 100 && (
              <div className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-1.5">
                <Sparkles className="w-4 h-4" /> {discount}% referral partner discount applied
              </div>
            )}

            <ul className="space-y-2 text-slate-700">
              {trialAvailable && (
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> {trialDays}-day free trial, then {money(plan.payable)}/{billingInterval === "year" ? "yr" : "mo"} — cancel anytime before it ends to avoid charges</li>
              )}
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Unlimited Due Diligence property reports</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Unlimited AI property price estimates</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Unlimited AI property discussions</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Comprehensive third-party property data</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Saved report history across sessions</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#547d91]" /> Cancel anytime</li>
            </ul>

            <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
              {trialAvailable
                ? `Your card is saved securely now but not charged during the ${trialDays}-day trial. After the trial you'll be billed ${money(plan.payable)}/${billingInterval === "year" ? "yr" : "mo"} (incl. GST)${discount > 0 && discount < 100 ? ` — includes your ${discount}% referral discount` : ""}. Cancel anytime from your billing portal.`
                : `Billed ${billingInterval === "year" ? "annually" : "monthly"} at ${money(plan.payable)} (incl. GST)${discount > 0 && discount < 100 ? ` — includes your ${discount}% referral discount` : ""}. Auto-renews each ${billingInterval === "year" ? "year" : "month"}; cancel anytime from your billing portal.`}
            </div>

            <Button onClick={handleSubscribe} disabled={checkingOut} className="w-full sm:w-auto" data-testid="subscribe-btn">
              {checkingOut ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to checkout...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> {trialAvailable ? `Start ${trialDays}-day free trial` : "Subscribe now"}</>
              )}
            </Button>

            {/* Referral code entry */}
            <div className="border-t border-slate-100 pt-3">
              <div className="space-y-2" data-testid="referral-code-section">
                <Label htmlFor="referral-code" className="text-sm text-slate-700">Have a referral code?</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="referral-code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    placeholder="e.g. CODE123"
                    className="sm:max-w-xs"
                    data-testid="referral-code-input"
                    onKeyDown={(e) => { if (e.key === "Enter") handleApplyReferral(); }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyReferral}
                    disabled={applyingCode}
                    data-testid="apply-referral-code-btn"
                  >
                    {applyingCode ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</> : "Apply code"}
                  </Button>
                </div>
                <p className="text-xs text-slate-400">Enter a partner code to unlock a discount or an extended free trial, then subscribe.</p>
              </div>
            </div>


            <p className="text-xs text-slate-400 border-t border-slate-100 pt-3" data-testid="lite-note">
              Not ready to upgrade? You'll stay on <span className="font-medium text-slate-500">Pocket BA Lite</span> — free access to everything except the AI Buyers Agent. Downgrade anytime and keep your Lite access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // HAS ACCESS
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {Header}
        <div className="flex items-center gap-2 flex-wrap">
          {reason === "admin" && <Badge variant="secondary">Admin access</Badge>}
          {reason === "referral_free" && <Badge variant="secondary">Included via partner</Badge>}
          {reason === "subscription" && (
            <>
              {isTrialing
                ? <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Free trial</Badge>
                : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Subscribed</Badge>}
              <Badge variant="secondary" data-testid="plan-interval-badge">
                {currentInterval === "year" ? "Annual" : "Monthly"}
              </Badge>
            </>
          )}
        </div>
      </div>

      {showAnnualNudge && (
        <Card className="border-emerald-200 bg-emerald-50/60" data-testid="annual-nudge">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Switch to Annual and save {annualSavingsPct}%</p>
                <p className="text-sm text-slate-600">
                  Pay {money(annualPlan.payable)}/yr instead of {money(monthlyPlan.payable * 12)} — that's just{" "}
                  {money(annualPerMonth)}/mo.{isTrialing ? " No charge during your trial." : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" onClick={handleSwitchToAnnual} disabled={managingPlan} data-testid="switch-annual-btn" className="bg-emerald-600 hover:bg-emerald-700">
                {managingPlan ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Switching...</> : "Switch to Annual"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setNudgeDismissed(true)} data-testid="dismiss-nudge-btn">
                Not now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {cancelAtPeriodEnd && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 -mt-2" data-testid="cancel-notice">
          Your subscription is set to cancel. You'll keep access until{" "}
          {isTrialing && trialEnd ? formatDate(trialEnd) : formatDate(sub?.current_period_end)}, then move to Pocket BA Lite.
          Changed your mind? Use "Resume subscription".
        </p>
      )}

      {/* Generator */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
                  <Search className="w-4 h-4 text-[#547d91]" />
                </div>
                Due Diligence Report
              </CardTitle>
              <p className="text-sm text-slate-500 mt-2">
                Third-party property due diligence data compiled from public and government sources. Provided for
                general information only — verify all details independently and seek professional advice before
                relying on it for any decision.
              </p>
            </div>
            <RequestCallButton tool="Due Diligence Report" defaultAddress={address} resultSummary={buildReportOutline(selectedReport)} className="shrink-0" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address">Property Address</Label>
            <AddressAutocomplete
              id="address"
              data-testid="researcher-address-input"
              placeholder="e.g. 68 Fairfield Road, Fairfield QLD"
              value={address}
              onChange={(v) => setAddress(v)}
              onAddressSelect={(a) => setAddress(a.formattedAddress || a.streetAddress)}
              onKeyDown={(e) => e.key === "Enter" && !submittingText && handleGenerateText()}
            />
            <p className="text-xs text-slate-500">
              Include the suburb and state for an accurate match (fuzzy matching is supported).
            </p>
          </div>

          <Button
            onClick={handleGenerateText}
            disabled={submittingText || textLimitReached}
            data-testid="researcher-generate-btn"
            className="w-full sm:w-auto"
          >
            {submittingText ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating report...</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Generate Report</>
            )}
          </Button>

          <div className="space-y-1">
            {hasTextLimit && (
              <p className={`text-xs ${textLimitReached ? "text-red-600" : "text-slate-500"}`} data-testid="text-reports-quota-note">
                {textLimitReached
                  ? `Monthly limit of ${textInfo.limit} reports reached. Resets at the start of next month.`
                  : `${textRemaining} of ${textInfo.limit} reports remaining this month.`}
              </p>
            )}
            <p className="text-xs text-slate-400">
              Covers planning, hazards, environment, utilities, roads &amp; rail and neighbourhood insights for the property — generated instantly.
            </p>
          </div>

          {/* Previously generated reports (collapsible) */}
          <div className="border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setShowHistory((s) => !s)}
              data-testid="toggle-report-history"
              className="w-full flex items-center justify-between text-sm font-medium text-slate-700 hover:text-[#547d91] transition-colors"
            >
              <span>Previously generated reports {reports.length > 0 ? `(${reports.length})` : ""}</span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showHistory && (
              <div className="mt-3" data-testid="report-history-panel">
                {loadingReports ? (
                  <div className="py-6 flex justify-center text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : reports.length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-400">
                    No reports yet. Generate your first property report above.
                  </p>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                    {reports.map((report) => (
                      <div
                        key={report.report_id}
                        className="flex items-center justify-between gap-3 p-3"
                        data-testid="research-report-row"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{report.address}</p>
                          <p className="text-xs text-slate-500">{formatDate(report.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => openReport(report)} data-testid="report-view-btn">
                            View
                          </Button>
                          {report.report_type !== "text" && report.report_url && (
                            <a href={report.report_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(report.report_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inline viewer */}
      {selectedReport && (
        <Card ref={viewerRef} data-testid="report-viewer">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="truncate">{selectedReport.resolved_address || selectedReport.address}</span>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                {selectedReport.report_type === "text" ? (
                  <span className="text-xs font-normal text-slate-500 flex items-center gap-1">
                    <AlignLeft className="w-3.5 h-3.5" /> Text report
                  </span>
                ) : (
                  <a
                    href={selectedReport.report_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#547d91] hover:underline flex items-center gap-1"
                  >
                    Open in new tab <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                  data-testid="report-viewer-close"
                >
                  Close
                </button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedReport.report_type === "text" ? (
              <TextReportView report={selectedReport} />
            ) : (
              <>
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                  <iframe
                    title="Property Report"
                    src={selectedReport.report_url}
                    className="w-full"
                    style={{ height: "70vh" }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  If the report is still generating, this page will refresh automatically when it&apos;s ready.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Price Estimate (Direct Comparison) */}
      <PriceEstimateTool />

      {/* AI Rental Estimate */}
      <RentalEstimateTool />

      {/* AI Property Advisor */}
      <AiAdvisorChat reports={reports} />
    </div>
  );
}
